<template>
  <section
    class="relative min-h-[60vh] pt-5 sm:pt-20 flex items-center bg-white dark:bg-gray-900 overflow-hidden"
  >
    <!-- Video Background -->
    <div class="absolute top-0 left-0 w-full h-full z-0">
      <iframe
        class="absolute top-1/2 left-1/2 w-full h-full object-cover transform -translate-x-1/2 -translate-y-1/2"
        src="https://www.youtube.com/embed/ZnGP4aPpA8E?autoplay=1&mute=1&loop=1&playlist=ZnGP4aPpA8E&controls=0&showinfo=0&autohide=1&modestbranding=1&vq=hd1080"
        frameborder="0"
        allow="autoplay; encrypted-media"
        allowfullscreen
      ></iframe>
      <div
        class="absolute top-0 left-0 w-full h-full bg-white opacity-80"
      ></div>
    </div>

    <!-- Main Content -->
    <div class="container mx-auto px-4 relative z-10 pb-20">
      <div class="max-w-5xl mx-auto">
        <!-- Hero Content -->
        <div class="text-center mb-1 relative">
          <h1
            class="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent"
          >
            Your One Stop for
            <transition name="flip" mode="out-in">
              <span :key="currentText" class="block mt-2 text-primary">
                {{ currentText }}
              </span>
            </transition>
          </h1>
          <p
            class="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-8"
          >
            Join a vibrant community of scholars, researchers, and enthusiasts.
            Stay updated with the latest academic opportunities and connections.
          </p>
        </div>

        <!-- Action Buttons -->
        <div
          class="flex flex-col sm:flex-row justify-center gap-4 mb-5 relative"
        >
          <nuxt-link
            to="/events"
            class="group relative inline-flex items-center justify-center px-8 py-4 text-base font-medium bg-primary text-white rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/10"
          >
            <Icon name="heroicons:calendar" class="w-5 h-5 mr-2" />
            Explore Events
          </nuxt-link>

          <nuxt-link
            to="/become-a-member"
            class="group relative inline-flex items-center justify-center px-8 py-4 text-base font-medium bg-primary/10 text-primary border border-primary/20 rounded-xl hover:bg-primary/20 transition-all"
          >
            <Icon name="heroicons:star" class="w-5 h-5 mr-2" />
            Join our community
          </nuxt-link>
        </div>

        <div class="flex items-center justify-center mb-5 text-sm">
          <div class="h-[1px] w-16 bg-gray-200 dark:bg-gray-700"></div>
          <span class="px-4 text-gray-400 dark:text-gray-500 font-medium"
            >or</span
          >
          <div class="h-[1px] w-16 bg-gray-200 dark:bg-gray-700"></div>
        </div>

        <!-- Search Bar -->
        <div class="max-w-3xl mx-auto mb-10">
          <!-- Changed from max-w-2xl -->
          <form @submit.prevent="handleSearch" class="relative">
            <label for="event-search" class="sr-only">Search events</label>
            <div class="relative">
              <input
                id="event-search"
                v-model="searchQuery"
                type="search"
                name="search"
                placeholder="eg: World Con "
                class="w-full px-6 py-4 text-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm focus:ring-2 focus:ring-primary focus:border-transparent pl-14"
              />
              <div
                class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
              >
                <Icon
                  name="heroicons:magnifying-glass"
                  class="w-6 h-6 text-gray-400"
                />
              </div>
              <button
                type="submit"
                class="absolute inset-y-2 right-2 px-4 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
              >
                Search
              </button>
            </div>
            <div
              class="mt-3 flex flex-wrap gap-3 justify-center items-center text-sm"
            >
              <span class="text-gray-600 dark:text-gray-400 font-medium"
                >Popular:</span
              >
              <div class="flex flex-wrap gap-2 justify-center">
                <button
                  v-for="tag in popularTags"
                  :key="tag"
                  type="button"
                  @click="searchByTag(tag)"
                  class="px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 hover:border-primary hover:bg-primary/5 text-gray-600 dark:text-gray-300 hover:text-primary transition-all duration-200 flex items-center gap-1"
                >
                  <Icon name="heroicons:tag" class="w-4 h-4" />
                  {{ tag }}
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Incubation Center Notice -->

        <UStats></UStats>
      </div>
    </div>
  </section>
</template>

<script setup>
const words = [
  "Academic Updates",
  "Research Events",
  "Academic Listings",
  "Academic Buzz",
];
const currentText = ref(words[0]);
let currentIndex = 0;

onMounted(() => {
  setInterval(() => {
    currentIndex = (currentIndex + 1) % words.length;
    currentText.value = words[currentIndex];
  }, 3000); // Change text every 3 seconds
});

const router = useRouter();
const searchQuery = ref("");
const popularTags = [
  "Dental",
  "International",
  "Space",
  "Orthodontic",
  "Nursing",
];

const handleSearch = () => {
  if (searchQuery.value.trim()) {
    router.push({
      path: "/events",
      query: { search: searchQuery.value },
    });
  }
};

const searchByTag = (tag) => {
  searchQuery.value = tag;
  handleSearch();
};
</script>

<style>
.bg-grid-pattern {
  background-image: radial-gradient(
    circle at 1px 1px,
    rgb(0 0 0 / 0.1) 1px,
    transparent 0
  );
  background-size: 24px 24px;
}

.bg-dot-pattern {
  background-image: radial-gradient(
    circle at 2px 2px,
    rgb(0 0 0 / 0.07) 1.5px,
    transparent 0
  );
  background-size: 32px 32px;
  background-position: -1px -1px;
}

.bg-circuit-pattern {
  background-image: linear-gradient(
      to right,
      rgb(0 0 0 / 0.05) 1px,
      transparent 1px
    ),
    linear-gradient(to bottom, rgb(0 0 0 / 0.05) 1px, transparent 1px);
  background-size: 64px 64px;
}

/* Dark mode pattern adjustments */
@media (prefers-color-scheme: dark) {
  .bg-dot-pattern {
    background-image: radial-gradient(
      circle at 2px 2px,
      rgb(255 255 255 / 0.07) 1.5px,
      transparent 0
    );
  }

  .bg-circuit-pattern {
    background-image: linear-gradient(
        to right,
        rgb(255 255 255 / 0.05) 1px,
        transparent 1px
      ),
      linear-gradient(to bottom, rgb(255 255 255 / 0.05) 1px, transparent 1px);
  }
}

.flip-enter-active,
.flip-leave-active {
  transition: transform 0.6s, opacity 0.6s;
}

.flip-enter-from {
  transform: rotateX(90deg);
  opacity: 0;
}

.flip-leave-to {
  transform: rotateX(-90deg);
  opacity: 0;
}

.flip-enter-to,
.flip-leave-from {
  transform: rotateX(0deg);
  opacity: 1;
}
</style>

<style>
/* Replace existing pattern styles with these */
.pattern-dots {
  background: repeating-radial-gradient(
    circle at 50% 50%,
    rgba(0, 0, 0, 0.03) 0,
    rgba(0, 0, 0, 0.03) 1px,
    transparent 1px,
    transparent 100%
  );
  background-size: 20px 20px;
  animation: patternMove 20s linear infinite;
}

.pattern-lines {
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.02) 1px, transparent 1px),
    linear-gradient(rgba(0, 0, 0, 0.02) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: patternMove 15s linear infinite reverse;
}

.pattern-grid {
  background: linear-gradient(
      to right,
      rgba(0, 0, 0, 0.01) 1px,
      transparent 1px
    ),
    linear-gradient(to bottom, rgba(0, 0, 0, 0.01) 1px, transparent 1px);
  background-size: 30px 30px;
  animation: patternMove 25s linear infinite;
}

@keyframes patternMove {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 100px 100px;
  }
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .pattern-dots {
    background: repeating-radial-gradient(
      circle at 50% 50%,
      rgba(255, 255, 255, 0.03) 0,
      rgba(255, 255, 255, 0.03) 1px,
      transparent 1px,
      transparent 100%
    );
  }

  .pattern-lines {
    background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.02) 1px,
        transparent 1px
      ),
      linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px);
  }

  .pattern-grid {
    background: linear-gradient(
        to right,
        rgba(255, 255, 255, 0.01) 1px,
        transparent 1px
      ),
      linear-gradient(to bottom, rgba(255, 255, 255, 0.01) 1px, transparent 1px);
  }
}

/* Keep only the flip animation styles */
.flip-enter-active,
.flip-leave-active {
  transition: transform 0.6s, opacity 0.6s;
}

.flip-enter-from {
  transform: rotateX(90deg);
  opacity: 0;
}

.flip-leave-to {
  transform: rotateX(-90deg);
  opacity: 0;
}

.flip-enter-to,
.flip-leave-from {
  transform: rotateX(0deg);
  opacity: 1;
}
</style>

<style scoped>
.bg-pattern {
  background-image: linear-gradient(
      30deg,
      #000 12%,
      transparent 12.5%,
      transparent 87%,
      #000 87.5%,
      #000
    ),
    linear-gradient(
      150deg,
      #000 12%,
      transparent 12.5%,
      transparent 87%,
      #000 87.5%,
      #000
    ),
    linear-gradient(
      30deg,
      #000 12%,
      transparent 12.5%,
      transparent 87%,
      #000 87.5%,
      #000
    ),
    linear-gradient(
      150deg,
      #000 12%,
      transparent 12.5%,
      transparent 87%,
      #000 87.5%,
      #000
    ),
    linear-gradient(
      60deg,
      #000 25%,
      transparent 25.5%,
      transparent 75%,
      #000 75%,
      #000
    ),
    linear-gradient(
      60deg,
      #000 25%,
      transparent 25.5%,
      transparent 75%,
      #000 75%,
      #000
    );
  background-size: 80px 140px;
  background-position: 0 0, 0 0, 40px 70px, 40px 70px, 0 0, 40px 70px;
}

@media (prefers-color-scheme: dark) {
  .bg-pattern {
    background-image: linear-gradient(
        30deg,
        #fff 12%,
        transparent 12.5%,
        transparent 87%,
        #fff 87.5%,
        #fff
      ),
      linear-gradient(
        150deg,
        #fff 12%,
        transparent 12.5%,
        transparent 87%,
        #fff 87.5%,
        #fff
      ),
      linear-gradient(
        30deg,
        #fff 12%,
        transparent 12.5%,
        transparent 87%,
        #fff 87.5%,
        #fff
      ),
      linear-gradient(
        150deg,
        #fff 12%,
        transparent 12.5%,
        transparent 87%,
        #fff 87.5%,
        #fff
      ),
      linear-gradient(
        60deg,
        #fff 25%,
        transparent 25.5%,
        transparent 75%,
        #fff 75%,
        #fff
      ),
      linear-gradient(
        60deg,
        #fff 25%,
        transparent 25.5%,
        transparent 75%,
        #fff 75%,
        #fff
      );
  }
}
</style>
<style scoped>
  iframe {
    width: 100vw;
    height: 56.25vw; /* Given a 16:9 aspect ratio, 9/16*100 = 56.25 */
    min-height: 100vh;
    min-width: 177.77vh; /* Given a 16:9 aspect ratio, 16/9*100 = 177.77 */
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
</style>
